import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

async function fetchApi(url, options = {}) {
  const res = await fetch(url, options);
  if (!res.ok) {
    let msg = res.statusText;
    try { const j = await res.json(); msg = j.message || msg; } catch (_) {}
    throw new Error(msg);
  }
  return res.status === 204 ? null : res.json();
}

function AppSettingsPage() {
  const { appId } = useParams();
  const { token, isFullAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // App name (admin DB)
  const [appName, setAppName] = useState('');
  const [savingAppName, setSavingAppName] = useState(false);

  // Admin DB: App credentials and direct PG connection
  const [supabaseUrl, setSupabaseUrl] = useState('');
  const [supabaseServiceRoleKey, setSupabaseServiceRoleKey] = useState('');
  const [pgHost, setPgHost] = useState('');
  const [pgPort, setPgPort] = useState('');
  const [pgUser, setPgUser] = useState('');
  const [pgPassword, setPgPassword] = useState('');
  const [pgDbname, setPgDbname] = useState('');
  const [savingCredentials, setSavingCredentials] = useState(false);

  // App config (tenant DB)
  const [config, setConfig] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // 1) Load current app details (admin DB)
      const appDetails = await fetchApi(`${API_BASE_URL}/apps/${appId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAppName(appDetails?.name || '');
      // Fill connection fields from admin DB
      setSupabaseUrl(appDetails?.supabase_url || '');
      setSupabaseServiceRoleKey(appDetails?.supabase_service_role_key || '');
      setPgHost(appDetails?.pg_host || '');
      setPgPort(appDetails?.pg_port ? String(appDetails.pg_port) : '');
      setPgUser(appDetails?.pg_user || '');
      setPgPassword(appDetails?.pg_password || '');
      setPgDbname(appDetails?.pg_dbname || '');

      // 2) Load app config (tenant DB)
      const cfg = await fetchApi(`${API_BASE_URL}/apps/${appId}/app-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfig(cfg || {});
    } catch (e) {
      setError(e.message || 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, [appId, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveAppName = async () => {
    setSavingAppName(true);
    setError('');
    try {
      await fetchApi(`${API_BASE_URL}/apps/${appId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: appName }),
      });
    } catch (e) {
      setError(e.message || 'Failed to save app name.');
    } finally {
      setSavingAppName(false);
    }
  };

  const handleSaveCredentials = async () => {
    setSavingCredentials(true);
    setError('');
    try {
      const payload = {
        supabase_url: supabaseUrl,
        supabase_service_role_key: supabaseServiceRoleKey,
        pg_host: pgHost || null,
        pg_port: pgPort ? Number(pgPort) : null,
        pg_user: pgUser || null,
        pg_password: pgPassword || null,
        pg_dbname: pgDbname || null,
      };
      await fetchApi(`${API_BASE_URL}/apps/${appId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
    } catch (e) {
      setError(e.message || 'Failed to save credentials.');
    } finally {
      setSavingCredentials(false);
    }
  };

  const handleToggle = (key) => (event) => {
    const val = event.target.checked ? 'true' : 'false';
    setConfig((prev) => ({ ...prev, [key]: val }));
  };

  const handleChange = (key) => (event) => {
    setConfig((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    setError('');
    try {
      await fetchApi(`${API_BASE_URL}/apps/${appId}/app-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(config),
      });
    } catch (e) {
      setError(e.message || 'Failed to save configuration.');
    } finally {
      setSavingConfig(false);
    }
  };

  const boolAsChecked = (val) => String(val).toLowerCase() === 'true';

  const handleDownloadSqlBackup = async () => {
    setError('');
    setDownloadingBackup(true);
    try {
      const res = await fetch(`${API_BASE_URL}/apps/${appId}/backup/sql`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let msg = res.statusText;
        try { const j = await res.json(); msg = j.message || msg; } catch (_) {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const cd = res.headers.get('content-disposition');
      let filename = `app-${appId}-backup.sql`;
      if (cd) {
        const m = cd.match(/filename=\"?([^\";]+)\"?/i);
        if (m) filename = m[1];
      }
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Failed to download SQL backup.');
    } finally {
      setDownloadingBackup(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
        App Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Admin Panel App Info
            </Typography>
            <TextField
              label="App Name (Admin DB)"
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Button variant="contained" onClick={handleSaveAppName} disabled={!isFullAdmin() || savingAppName}>
              {savingAppName ? 'Saving…' : 'Save Name'}
            </Button>
            {!isFullAdmin() && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Only full admins can update the app name in the admin database.
              </Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              App Configuration (Tenant DB)
            </Typography>

            <TextField
              label="REVENUECAT_API_KEY_ANDROID"
              fullWidth
              value={config.REVENUECAT_API_KEY_ANDROID || ''}
              onChange={handleChange('REVENUECAT_API_KEY_ANDROID')}
              sx={{ mb: 2 }}
            />
            <TextField
              label="REVENUECAT_API_KEY_IOS"
              fullWidth
              value={config.REVENUECAT_API_KEY_IOS || ''}
              onChange={handleChange('REVENUECAT_API_KEY_IOS')}
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />

            <FormControlLabel
              control={<Switch checked={boolAsChecked(config.EXPO_PUBLIC_DISABLE_CHAT)} onChange={handleToggle('EXPO_PUBLIC_DISABLE_CHAT')} />}
              label="Disable Chat"
            />
            <FormControlLabel
              control={<Switch checked={boolAsChecked(config.EXPO_PUBLIC_DISABLE_DORFBOT)} onChange={handleToggle('EXPO_PUBLIC_DISABLE_DORFBOT')} />}
              label="Disable Dorfbot"
            />
            <FormControlLabel
              control={<Switch checked={boolAsChecked(config.EXPO_PUBLIC_DISABLE_MAP)} onChange={handleToggle('EXPO_PUBLIC_DISABLE_MAP')} />}
              label="Disable Map"
            />
            <FormControlLabel
              control={<Switch checked={boolAsChecked(config.EXPO_PUBLIC_DISABLE_PREFERENCES)} onChange={handleToggle('EXPO_PUBLIC_DISABLE_PREFERENCES')} />}
              label="Disable Preferences"
            />
            <FormControlLabel
              control={<Switch checked={boolAsChecked(config.EXPO_PUBLIC_ENABLE_ANDROID_IAP)} onChange={handleToggle('EXPO_PUBLIC_ENABLE_ANDROID_IAP')} />}
              label="Enable Android IAP"
            />
            <FormControlLabel
              control={<Switch checked={boolAsChecked(config.EXPO_PUBLIC_ENABLE_IOS_IAP)} onChange={handleToggle('EXPO_PUBLIC_ENABLE_IOS_IAP')} />}
              label="Enable iOS IAP"
            />

            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={handleSaveConfig} disabled={savingConfig}> 
                {savingConfig ? 'Saving…' : 'Save Configuration'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              App Database Connections (Admin DB)
            </Typography>

            <TextField
              label="Supabase URL"
              fullWidth
              value={supabaseUrl}
              onChange={(e) => setSupabaseUrl(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Supabase Service Role Key"
              type="password"
              fullWidth
              value={supabaseServiceRoleKey}
              onChange={(e) => setSupabaseServiceRoleKey(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="PostgreSQL Host"
                  fullWidth
                  value={pgHost}
                  onChange={(e) => setPgHost(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="PostgreSQL Port"
                  type="number"
                  fullWidth
                  value={pgPort}
                  onChange={(e) => setPgPort(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="PostgreSQL User"
                  fullWidth
                  value={pgUser}
                  onChange={(e) => setPgUser(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="PostgreSQL Password"
                  type="password"
                  fullWidth
                  value={pgPassword}
                  onChange={(e) => setPgPassword(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="PostgreSQL Database Name"
                  fullWidth
                  value={pgDbname}
                  onChange={(e) => setPgDbname(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained"
                onClick={handleSaveCredentials}
                disabled={!isFullAdmin() || savingCredentials}
              >
                {savingCredentials ? 'Saving…' : 'Save Connections'}
              </Button>
              <Button
                sx={{ ml: 2 }}
                variant="outlined"
                onClick={handleDownloadSqlBackup}
                disabled={!isFullAdmin() || downloadingBackup}
              >
                {downloadingBackup ? 'Preparing Backup…' : 'Download SQL Backup'}
              </Button>
            </Box>

            {!isFullAdmin() && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Only full admins can update database connections, Supabase credentials, and download database backups.
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AppSettingsPage;

